import React, { useState } from "react";
import { Popover, Paper, Stack, Typography, IconButton, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from '@mui/icons-material/Close';

type QuickMenuProps = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  suggestions: string[];
  onPick: (text: string) => void;
  onDelete?: (text: string) => void;
  onReorder?: (suggestions: string[]) => void;
};

const StyledPaper = styled(Paper)({
  backgroundColor: '#2a2a2a',
  border: '1px solid #404040',
  borderRadius: '8px',
  padding: '12px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
  maxWidth: '350px',
});

const StyledTitle = styled(Typography)({
  color: '#4A9EFF',
  fontSize: '11px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: '8px',
});

const ChipContainer = styled(Box)<{ isDragging?: boolean }>(({ isDragging }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor: isDragging ? '#3a3a3a' : '#353535',
  border: '1px solid #4a4a4a',
  borderRadius: '6px',
  margin: '2px',
  cursor: isDragging ? 'grabbing' : 'grab',
  userSelect: 'none',
  transition: 'all 0.2s ease',
  opacity: isDragging ? 0.7 : 1,
  transform: isDragging ? 'rotate(2deg)' : 'rotate(0deg)',
  
  '&:hover': {
    backgroundColor: '#404040',
    borderColor: '#555555',
  }
}));

const ChipText = styled('span')({
  padding: '6px 8px',
  fontSize: '13px',
  color: '#e0e0e0',
  cursor: 'pointer',
  
  '&:hover': {
    color: '#4A9EFF',
  }
});

const DeleteButton = styled(IconButton)({
  padding: '4px',
  marginRight: '2px',
  color: '#888',
  borderRadius: '4px',
  
  '&:hover': {
    color: '#ff6b6b',
    backgroundColor: 'rgba(255, 107, 107, 0.1)',
  },
  
  '& .MuiSvgIcon-root': {
    fontSize: '14px',
  }
});

const QuickMenu: React.FC<QuickMenuProps> = ({ 
  anchorEl, 
  onClose, 
  suggestions, 
  onPick, 
  onDelete,
  onReorder 
}) => {
  const open = Boolean(anchorEl);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [draggedOverIndex, setDraggedOverIndex] = useState<number | null>(null);

  const handleDragStart = (e: React.DragEvent, suggestion: string) => {
    setDraggedItem(suggestion);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', suggestion);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDraggedOverIndex(index);
  };

  const handleDragLeave = () => {
    setDraggedOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedItem) {
      const draggedIndex = suggestions.indexOf(draggedItem);
      if (draggedIndex !== -1 && draggedIndex !== dropIndex) {
        const newSuggestions = [...suggestions];
        newSuggestions.splice(draggedIndex, 1);
        newSuggestions.splice(dropIndex, 0, draggedItem);
        onReorder?.(newSuggestions);
      }
    }
    
    setDraggedItem(null);
    setDraggedOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDraggedOverIndex(null);
  };

  const handleDelete = (e: React.MouseEvent, suggestion: string) => {
    e.stopPropagation();
    e.preventDefault();
    onDelete?.(suggestion);
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "left" }}
      transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      PaperProps={{ 
        elevation: 0,
        style: { backgroundColor: 'transparent' }
      }}
      transitionDuration={150}
    >
      <StyledPaper>
        <StyledTitle>
          Domande salvate
        </StyledTitle>
        
        <Stack
          direction="row"
          gap={0.5}
          flexWrap="wrap"
        >
          {suggestions.map((suggestion, index) => (
            <ChipContainer
              key={suggestion}
              isDragging={draggedItem === suggestion}
              draggable
              onDragStart={(e) => handleDragStart(e, suggestion)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              sx={{
                backgroundColor: draggedOverIndex === index ? '#404040' : undefined,
                borderColor: draggedOverIndex === index ? '#4A9EFF' : undefined,
              }}
            >
              <ChipText
                onClick={() => { 
                  onPick(suggestion); 
                  onClose(); 
                }}
              >
                {suggestion}
              </ChipText>
              
              {onDelete && (
                <DeleteButton
                  size="small"
                  onClick={(e) => handleDelete(e, suggestion)}
                >
                  <CloseIcon />
                </DeleteButton>
              )}
            </ChipContainer>
          ))}
        </Stack>
      </StyledPaper>
    </Popover>
  );
};

export default QuickMenu;